import { intervalToDuration } from 'date-fns/intervalToDuration';
import dayjs, { isDayjs } from 'dayjs';

export const getTimezoneOffset = (date: Date): string => {
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '';
  return `GMT${sign}${offset / 60}`;
};

export const getTimezoneOffsetHours = (timeZone: string, date: Date = new Date()): number => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return (tzDate.getTime() - utcDate.getTime()) / 3600000;
};

export const getLocalHourFromCanonical = (
  canonicalHour: number,
  canonicalTz: string,
  referenceDate: Date = new Date(),
): number => {
  const canonicalOffsetHours = getTimezoneOffsetHours(canonicalTz, referenceDate);
  const localOffsetHours = -referenceDate.getTimezoneOffset() / 60;
  const localHour = canonicalHour - canonicalOffsetHours + localOffsetHours;
  return ((localHour % 24) + 24) % 24;
};

export const formatDate = ({
  date,
  defaultText,
  locale = 'en-GB',
  hasTime,
  hasTimezone,
  yearAfterComma = false,
}: {
  date: Date | undefined;
  defaultText?: string;
  locale?: string;
  hasTime?: boolean;
  hasTimezone?: boolean;
  yearAfterComma?: boolean;
}): string => {
  if (!date) return defaultText ?? '';

  let dateStr = date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (yearAfterComma) {
    dateStr = dateStr.replace(/ (\d{4})$/, ', $1');
  }

  const timeStr = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const timezoneStr = hasTimezone ? ` (${getTimezoneOffset(date)})` : '';

  let result = dateStr;

  if (hasTime) {
    result += ` - ${timeStr}`;
  }

  if (hasTimezone) {
    result += ` ${timezoneStr}`;
  }

  return result;
};

// Converts a date string (YYYY-MM-DD or ISO 8601) or Date object to a Date object
export const getDate = (dateInput: Date | string | undefined): Date | undefined => {
  if (typeof dateInput === 'string') {
    return new Date(dateInput);
  }
  return dateInput;
};

export const getTimeWithTimeZone = (date: Date, locale = 'en-GB'): string => {
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const getDateWithTimeZone = (date: Date, locale = 'en-GB'): string => {
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Recursively convert dates to ISO string format
export const convertDatesToISO = (obj: any, format = 'YYYY-MM-DDTHH:mm:ssZ'): any => {
  if (!obj) {
    return obj;
  }

  // Check if it's a Date object
  if (obj instanceof Date) {
    return dayjs(obj).format(format);
  }

  // Check if it's a dayjs object
  if (isDayjs(obj)) {
    return obj.format(format);
  }

  // If it's an array, recursively process each element
  if (Array.isArray(obj)) {
    return obj.map((item) => convertDatesToISO(item, format));
  }

  // If it's an object, recursively process each property
  if (typeof obj === 'object') {
    const converted: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        converted[key] = convertDatesToISO(obj[key], format);
      }
    }
    return converted;
  }

  // Return primitive values as-is
  return obj;
};

export const isCurrentDay = (date?: Date): boolean => {
  if (!date) return false;
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const getDurationLabel = (from: Date, to: Date): string => {
  const duration = intervalToDuration({ start: from, end: to });
  const units: Array<[string, number | undefined]> = [
    ['y', duration.years],
    ['mo', duration.months],
    ['d', duration.days],
    ['h', duration.hours],
    ['m', duration.minutes],
    ['s', duration.seconds],
  ];
  return (
    units
      .filter(([, value]) => value)
      .map(([unit, value]) => `${value}${unit}`)
      .join(' ') || '0s'
  );
};
